/* eslint-disable no-unused-vars */
import React, { useState } from 'react';

import Image from 'next/image';
import numWords from 'num-words';

import Button from '@/components/Button';
import { WizardStep } from '@/components/Wizard';
import { appUiIds } from '@/constants/appUiIds';
import useAppUiData from '@/hooks/useAppUiData';

import styles from './ClaimBookingStep.module.scss';
import ClaimBookingRadioGroup from './controls/ClaimBookingRadioGroup';
import { MatchingClient, StepData } from '../types/ClaimBookingTypes';

interface ClaimBookingSailorSelectProps {
  tabId: string;
  data: StepData;
  onSubmit: Function;
  onCancel: () => void;
  isUseRecaptchaV2: boolean;
  RecaptchaV2Component: React.ComponentType;
}

const ClaimBookingSailorSelect: React.FC<ClaimBookingSailorSelectProps> = ({
  tabId,
  data,
  onSubmit,
  onCancel,
  isUseRecaptchaV2,
  RecaptchaV2Component,
}) => {
  const appUiData = useAppUiData();
  const [selectedSailor, setSelectedSailor] = useState(data.sailors?.[0]);
  const noOfGuests = data?.noOfGuests ?? 0;
  const sailorsCount = data?.sailors?.length ?? 0;
  const hasUnnamedSailor = noOfGuests - sailorsCount > 0;

  const handleChange = (value: MatchingClient) => {
    setSelectedSailor(value);
  };

  const handleSubmit = async () => {
    const value = {
      bookingNumber: data.bookingNumber,
      user: {
        clientID: selectedSailor?.clientId,
        lastName: selectedSailor?.name?.last,
        birthday: selectedSailor?.dob,
      },
    };
    onSubmit(value);
  };

  return (
    <WizardStep tabId={tabId}>
      <h2 className={styles.title}>{appUiData[appUiIds.claimBooking.foundBookingTitle]}</h2>
      <p className={styles.subtitle}>
        {appUiData[appUiIds.claimBooking.foundBookingSubtitle].replace('{N}', numWords(data?.sailors?.length ?? 1))}
      </p>
      <form className={styles.selectForm}>
        <div className={styles.radioGroup}>
          <ClaimBookingRadioGroup
            sailors={data.sailors}
            selectedSailor={selectedSailor}
            onChange={handleChange}
            labelFunction={(sailor) => `${sailor.name?.first} ${sailor.name?.last}`}
          />
          {hasUnnamedSailor && (
            <div className={styles.unnamedSailorAlert}>
              <span className={styles.unnamedSailorHeader}>
                <p className={styles.unnamedSailorText}>Unnamed Sailor</p>
                <Image src="/auth/v1/static/image/disabledRadio.svg" width={20} height={20} alt="" />
              </span>
              <p className={styles.unnamedSailorInfo}>
                (If you are unnamed please contact your primary sailor or booking agent to be added to the booking
                before continuing)
              </p>
              <hr className={styles.radioDivider} />
            </div>
          )}
        </div>
        {isUseRecaptchaV2 && (
          <div className={styles.recaptchaContainer}>
            <RecaptchaV2Component />
          </div>
        )}
        <Button variant="primary" onClick={handleSubmit} disabled={false} className={styles.buttonSearch}>
          {appUiData[appUiIds.commonLabels.search]}
        </Button>
        <Button onClick={onCancel} className={styles.cancelLink}>
          {appUiData[appUiIds.commonLabels.cancel]}
        </Button>
      </form>
    </WizardStep>
  );
};

export default ClaimBookingSailorSelect;
